import {
  COMPOUND_SORTED,
  SIMPLE_COLOR_HEX,
  hexForColorToken,
  hexFromLastNamedToken,
  normalizeColorKey,
} from './colorDictionary'
import { normalizeTrustedHexInput } from './hexValidate'
import type { NormalizedColor, NormalizeColorOptions } from './types'

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i
const RGB_RE =
  /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+\s*)?\)$/i

function clamp255(n: number): number {
  return Math.max(0, Math.min(255, n))
}

function expandHex3(h: string): string {
  const a = h[0]
  const b = h[1]
  const c = h[2]
  return `#${a}${a}${b}${b}${c}${c}`.toUpperCase()
}

function toHex6(r: number, g: number, b: number): string {
  const h = (n: number) => clamp255(Math.round(n)).toString(16).padStart(2, '0')
  return `#${h(r)}${h(g)}${h(b)}`.toUpperCase()
}

function parseHexOrRgb(raw: string): { hex: string; source: 'hex' | 'rgb' } | null {
  const t = raw.trim()
  if (HEX_RE.test(t)) {
    const h = t.slice(1)
    const full = h.length === 3 ? expandHex3(h.toLowerCase()) : `#${h}`.toUpperCase()
    return { hex: full, source: 'hex' }
  }
  const m = t.match(RGB_RE)
  if (m) {
    return {
      hex: toHex6(Number(m[1]), Number(m[2]), Number(m[3])),
      source: 'rgb',
    }
  }
  return null
}

/** Insert spaces for camelCase (e.g. BleuMarine). Only ASCII A–Z counts as “upper”. */
function softenJoinedWords(s: string): string {
  return s
    .replace(/([a-zà-ÿ])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/(\d)([A-Za-z])/g, '$1 $2')
}

function preprocessLabel(raw: string): string {
  let s = String(raw ?? '').trim()
  if (!s) return ''
  s = softenJoinedWords(s)
  s = s.replace(/[_/]+/g, ' ').replace(/-/g, ' ').replace(/\s+/g, ' ').trim()
  return s
}

function lowUnresolved(label: string): NormalizedColor {
  return { label, hex: null, source: 'unresolved', confidence: 'low' }
}

function compoundPhraseMatches(full: string, phraseNorm: string): boolean {
  if (full === phraseNorm) return true
  if (full.startsWith(`${phraseNorm} `)) return true
  if (full.endsWith(` ${phraseNorm}`)) return true
  if (full.includes(` ${phraseNorm} `)) return true
  return false
}

/**
 * Résout une valeur couleur affichable avec niveau de confiance.
 * Ne génère **jamais** de teinte inventée (plus de fallback HSL) : `hex` null ⇒ pastille interdite.
 */
export function normalizeColor(raw: string, options?: NormalizeColorOptions): NormalizedColor {
  const label = String(raw ?? '').trim().replace(/\s+/g, ' ')
  const trusted = normalizeTrustedHexInput(String(options?.trustedHex ?? '').trim())
  if (trusted) {
    return {
      label: label || trusted,
      hex: trusted,
      source: 'trusted_hex',
      confidence: 'high',
    }
  }
  if (!label) {
    return lowUnresolved('')
  }

  const parsed = parseHexOrRgb(label)
  if (parsed) {
    return { label, hex: parsed.hex, source: parsed.source, confidence: 'high' }
  }

  const soft = preprocessLabel(label)
  const keyFull = normalizeColorKey(soft)

  for (const [phrase, hex] of COMPOUND_SORTED) {
    const pk = normalizeColorKey(phrase)
    if (compoundPhraseMatches(keyFull, pk)) {
      return {
        label,
        hex: hex.toUpperCase(),
        source: 'compound',
        confidence: 'medium',
      }
    }
  }

  const dictExact = SIMPLE_COLOR_HEX[keyFull]
  if (dictExact) {
    return {
      label,
      hex: dictExact.toUpperCase(),
      source: 'named',
      confidence: 'high',
    }
  }

  const tokens = keyFull.split(' ').filter(Boolean)
  if (tokens.length === 1) {
    const hex = hexForColorToken(tokens[0])
    if (hex) {
      return {
        label,
        hex: hex.toUpperCase(),
        source: 'named',
        confidence: 'high',
      }
    }
  }

  if (tokens.length >= 2) {
    const tailHex = hexFromLastNamedToken(tokens)
    if (tailHex) {
      return {
        label,
        hex: tailHex.toUpperCase(),
        source: 'named',
        confidence: 'medium',
      }
    }
  }

  return lowUnresolved(label)
}
